import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { SvgComponent } from '@shared/components/svg';
import { ClickOutsideDirective } from '@shared/directives';
import { Option } from '@shared/models';
import { UrlParamsService } from '@shared/services/url-params';

@Component({
  selector: 'app-filter-select',
  imports: [SvgComponent, NgClass, ClickOutsideDirective],
  templateUrl: './filter-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterSelectComponent {
  options = model.required<Option[]>();
  filterName = input.required<string>();
  dropdownPosition = input<'LEFT' | 'CENTER' | 'RIGHT'>('LEFT');
  displayAsBlock = input(false);
  theme = input<'STANDARD' | 'COLORED'>('STANDARD');
  placeholder = input<string>('');
  isOpen = signal(false);
  selectedKey = input<string>();
  readonly onFilterSelected = output<string>();
  optionsBackup!: Option[];
  firstInit = true;
  selected = computed(() => {
    return this.options().find((item) => item.isSelected === true) ?? undefined;
  });
  private readonly urlParamsService = inject(UrlParamsService);

  constructor() {
    effect(() => {
      if (this.firstInit) {
        this.optionsBackup = this.options();
        this.firstInit = false;
      }
      const paramKey = this.urlParamsService.param(this.filterName())();
      if (paramKey) {
        this.options.update((options) =>
          options.map((option) => ({
            ...option,
            isSelected: option.key === paramKey,
          }))
        );
      } else {
        this.options.set(this.optionsBackup);
      }
      const selected = this.options().find((option) => option.isSelected);
      if (selected) {
        this.urlParamsService.mergeParams({ [this.filterName()]: selected.key });
      }
    });
  }

  toggleSelect(): void {
    this.isOpen.update((o) => !o);
  }

  select(key: string) {
    this.urlParamsService.mergeParams({ [this.filterName()]: key });
    this.onFilterSelected.emit(key);
    this.isOpen.set(false);
  }
}
